// Frontend/src/contexts/LanguageContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { translations } from "@/i18n/translations";
import api, { getAccessToken } from '@/api/client';
import { toast } from 'sonner';

export type Language = "en" | "es";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language, persist?: boolean) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);


export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("app_language");
      if (stored === "es" || stored === "en") return stored;
    }
    return "es";
  });

  useEffect(() => {
    localStorage.setItem("app_language", language);
    // Update HTML lang attribute for accessibility
    document.documentElement.lang = language;
  }, [language]);
  const confirmedLanguage = useRef(language);
  const pendingSave = useRef(false);
  const saveQueue = useRef(Promise.resolve());
  const saveVersion = useRef(0);
  useEffect(() => {
    function restore(event: Event) {
      const value = (event as CustomEvent<Language>).detail;
      if ((value === 'en' || value === 'es') && !pendingSave.current) {
        confirmedLanguage.current = value;
        setLanguageState(value);
      }
    }
    window.addEventListener('urbe360:language', restore);
    return () => window.removeEventListener('urbe360:language', restore);
  }, []);

  const setLanguage = useCallback((lang: Language, persist = true) => {
    setLanguageState(lang);
    if (!persist) confirmedLanguage.current = lang;
    if (persist && getAccessToken()) {
      const version = ++saveVersion.current;
      pendingSave.current = true;
      // Serialize rapid choices so network arrival order cannot change the
      // final saved language. A failure is visible and rolls back the last pick.
      saveQueue.current = saveQueue.current.then(async () => {
        try {
          await api.patch('/auth/preferences/', { language: lang });
          confirmedLanguage.current = lang;
        }
        catch {
          if (version === saveVersion.current) {
            setLanguageState(confirmedLanguage.current);
            toast.error(translations[confirmedLanguage.current]['m3.preferences.saveError']);
          }
        }
        finally { if (version === saveVersion.current) pendingSave.current = false; }
      });
    }
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      let text = translations[language][key] || translations["en"][key] || key;
      
      // Replace parameters like {count}, {min}, {max}, etc.
      if (params) {
        Object.entries(params).forEach(([param, value]) => {
          text = text.replace(new RegExp(`\\{${param}\\}`, "g"), String(value));
        });
      }
      
      return text;
    },
    [language]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}

// Export translations for direct access if needed
export { translations };
